import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../../app/store';
import {
  DEFAULT_PREPAY_DISCOUNT_PERCENT,
  firstPlanVisitFeeCents,
  isAllInclusivePlanBooking,
  isPlanStartBasket,
  MobileServiceFeeConfig,
  PaymentPreference,
  PaymentPreferenceValue,
  planVisitFeeCents,
} from '../../util/bookingPricing';

export interface SlotPrice {
  /** Signed adjustment in cents for the selected slot (negative = discount). */
  cents: number;
  percent: number;
  tier: string | null;
  isLowestPrice: boolean;
}

/** Where the customer's booking-time payment stands (prepay bookings). */
export type PrepaymentUiState = 'UNPAID' | 'PAID' | 'SKIPPED';

/**
 * "Send the bill to your partner" once the booking exists: whom the pay
 * link went to (SENT), or that it could not be sent (FAILED).
 */
export interface PayerRequestState {
  status: 'SENT' | 'FAILED';
  email: string;
  name: string | null;
  changesLeft: number;
}

export interface BookingState {
  success: boolean;
  /** Id of the work order just created, for the payment step. */
  workOrderId: number | null;
  /** Signed token that lets the customer pay for that work order. */
  prepaymentToken: string | null;
  /**
   * A booking made without logging in used an email that already has an
   * account, and the booking went on it. True: the account is on this tenant
   * and the confirmation page offers (never requires) a login. False: it is
   * another brand's account, so no login or "set a password" is offered.
   * Null: a new account.
   */
  existingAccount: boolean | null;
  prepaymentState: PrepaymentUiState;
  /** Cents charged at booking once paid. */
  prepaidAmountCents: number;
  potentialFranchiseId?: number | null;
  vanId?: number | null;
  storedDiscountAmount?: number | null;
  storedDiscountType?: string | null;
  storedDiscountCodeId?: string | null;
  storedPromoCreditAmount?: number | null;
  /** Airline-style price adjustment for the chosen appointment time. */
  slotPrice: SlotPrice | null;
  /** How the customer chose to pay. Booking never requires payment. */
  paymentPreference: PaymentPreferenceValue;
  /**
   * True once the customer (or the fleet rule) has set `paymentPreference`
   * in this booking. While false the value is still the default and a tenant
   * preset may swap it (see `applyDefaultPaymentPreference`); once true an
   * explicit choice is never overridden.
   */
  paymentPreferenceTouched: boolean;
  /** Prepay discount percentage offered by the server. */
  prepayDiscountPercent: number;
  /** Best slot discount available (e.g. -15) for "from $X" prices. */
  bestSlotAdjustmentPercent: number;
  /** Name of the rule offering that discount, e.g. "Book 3+ days ahead". */
  bestSlotAdjustmentLabel: string | null;
  /** Hours before an appointment when lead-time discounts stop (e.g. 72). */
  discountCutoffHours: number | null;
  /** Sales tax for the service address, as a percentage (e.g. 12). */
  taxRatePercent: number | null;
  /** Mobile service fee charged on every visit, in cents. */
  mobileServiceFeeCents: number | null;
  /**
   * The All-Inclusive plan's own per-visit fee (its fee row), in cents, for
   * the visits inside the standard-fee allowance. Null when none is on file.
   */
  mobileServiceFeeAllInclusiveCents: number | null;
  /**
   * Fee per visit once the vehicle has used the standard-fee visits of its
   * plan year ("high-use"), in cents. Null when the fee never escalates.
   */
  mobileServiceFeeHighUseCents: number | null;
  /** Visits a plan year charged at the standard fee. Null when unlimited. */
  mobileServiceFeeVisitsIncluded: number | null;
  /** Visits this vehicle already had this plan year. Null when not tracked. */
  planYearVisitCount: number | null;
  /**
   * True when a plan's onboarding includes its first visit's mobile service
   * fee (Aperio): that visit is quoted, and charged, without it.
   */
  planStartMobileFeeIncluded: boolean;
  /**
   * A friend's referral code from the share link (`?ref=CODE`), sent with
   * the booking for the server to check and apply. See util/referralLink.
   */
  referralCode?: string | null;
  /**
   * This booking's idempotency key (util/bookingKey), sent with
   * createWorkOrder so a repeat of the same submit returns the same work
   * order. Made on the first "Lock in my booking", kept through retries and
   * reloads (sessionStorage), dropped once the booking exists.
   */
  idempotencyKey?: string | null;
  /**
   * "Send the bill to your partner" (Aperio preset only) is the chosen way
   * to pay. The booking itself is a prepay one (`paymentPreference` PREPAY,
   * prepay price); after it is made the pay link is emailed to the payer
   * instead of the booker being taken to the payment page.
   */
  payerSelected?: boolean;
  /** The payer's email and first name as typed on the booking step. */
  payerEmail?: string;
  payerName?: string;
  /** The pay link's email, once the booking exists. */
  payerRequest?: PayerRequestState | null;
}

const initialState: BookingState = {
  success: false,
  workOrderId: null,
  prepaymentToken: null,
  existingAccount: null,
  prepaymentState: 'UNPAID',
  prepaidAmountCents: 0,
  potentialFranchiseId: null,
  vanId: null,
  storedDiscountAmount: null,
  storedDiscountType: null,
  storedDiscountCodeId: null,
  storedPromoCreditAmount: null,
  slotPrice: null,
  paymentPreference: PaymentPreference.payLater,
  paymentPreferenceTouched: false,
  prepayDiscountPercent: DEFAULT_PREPAY_DISCOUNT_PERCENT,
  bestSlotAdjustmentPercent: 0,
  bestSlotAdjustmentLabel: null,
  discountCutoffHours: null,
  taxRatePercent: null,
  mobileServiceFeeCents: null,
  mobileServiceFeeAllInclusiveCents: null,
  mobileServiceFeeHighUseCents: null,
  mobileServiceFeeVisitsIncluded: null,
  planYearVisitCount: null,
  planStartMobileFeeIncluded: false,
  referralCode: null,
  idempotencyKey: null,
  payerSelected: false,
  payerEmail: '',
  payerName: '',
  payerRequest: null,
};

export const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    setBookingSuccess: (state, action: PayloadAction<boolean>) => ({
      ...state,
      success: action.payload,
    }),
    setPotentialFranchiseId: (state, action: PayloadAction<number | null>) => ({
      ...state,
      potentialFranchiseId: action.payload,
    }),
    setVanId: (state, action: PayloadAction<number | null>) => ({
      ...state,
      vanId: action.payload,
    }),
    setStoredDiscountAmount: (state, action: PayloadAction<number | null>) => ({
      ...state,
      storedDiscountAmount: action.payload,
    }),
    setStoredDiscountType: (state, action: PayloadAction<string | null>) => ({
      ...state,
      storedDiscountType: action.payload,
    }),
    setStoredDiscountCodeId: (
      state, action: PayloadAction<string | null>,
    ) => ({
      ...state,
      storedDiscountCodeId: action.payload,
    }),
    setStoredPromoCreditAmount: (state,
      action: PayloadAction<number | null>) => ({
      ...state,
      storedPromoCreditAmount: action.payload,
    }),
    setSlotPrice: (state, action: PayloadAction<SlotPrice | null>) => ({
      ...state,
      slotPrice: action.payload,
    }),
    setPaymentPreference: (
      state, action: PayloadAction<PaymentPreferenceValue>,
    ) => ({
      ...state,
      paymentPreference: action.payload,
      paymentPreferenceTouched: true,
    }),
    /**
     * A tenant's default for the booking step (the Aperio preset preselects
     * prepay). Only takes effect while the customer has not chosen yet, and
     * never counts as a choice itself, so the next booking gets the same
     * default again after `resetBookingPricing`.
     */
    applyDefaultPaymentPreference: (
      state, action: PayloadAction<PaymentPreferenceValue>,
    ) => (state.paymentPreferenceTouched
      ? state
      : { ...state, paymentPreference: action.payload }),
    /**
     * Prepay is not on offer for this booking (nobody serves the address,
     * or the server refused the payment): back to pay-at-visit. Not the
     * customer's choice, so `paymentPreferenceTouched` is left alone and a
     * tenant default can apply again once prepay is possible.
     */
    withdrawPrepay: (state) => (
      state.paymentPreference === PaymentPreference.payLater
        && !state.payerSelected
        ? state
        : {
          ...state,
          paymentPreference: PaymentPreference.payLater,
          // Sending the bill is a prepayment too: withdrawn with it.
          payerSelected: false,
        }
    ),
    /**
     * Choose (true) or leave (false) "Send the bill to your partner". The
     * payment preference itself is set with setPaymentPreference.
     */
    setPayerSelected: (state, action: PayloadAction<boolean>) => (
      !!state.payerSelected === action.payload
        ? state
        : { ...state, payerSelected: action.payload }
    ),
    setPayerDetails: (
      state,
      action: PayloadAction<{ email?: string; name?: string }>,
    ) => ({
      ...state,
      ...(action.payload.email !== undefined
        && { payerEmail: action.payload.email }),
      ...(action.payload.name !== undefined
        && { payerName: action.payload.name }),
    }),
    setPayerRequest: (
      state, action: PayloadAction<PayerRequestState | null>,
    ) => ({
      ...state,
      payerRequest: action.payload,
    }),
    setPrepayDiscountPercent: (state, action: PayloadAction<number>) => ({
      ...state,
      prepayDiscountPercent: action.payload,
    }),
    setBestSlotAdjustment: (
      state,
      action: PayloadAction<{
        percent: number;
        label: string | null;
        cutoffHours?: number | null;
        taxRatePercent?: number | null;
        mobileServiceFeeCents?: number | null;
        mobileServiceFeeAllInclusiveCents?: number | null;
        mobileServiceFeeHighUseCents?: number | null;
        mobileServiceFeeVisitsIncluded?: number | null;
        planYearVisitCount?: number | null;
        planStartMobileFeeIncluded?: boolean | null;
      }>,
    ) => ({
      ...state,
      bestSlotAdjustmentPercent: action.payload.percent,
      bestSlotAdjustmentLabel: action.payload.label,
      discountCutoffHours: action.payload.cutoffHours ?? null,
      taxRatePercent: action.payload.taxRatePercent ?? null,
      mobileServiceFeeCents: action.payload.mobileServiceFeeCents ?? null,
      mobileServiceFeeAllInclusiveCents:
        action.payload.mobileServiceFeeAllInclusiveCents ?? null,
      mobileServiceFeeHighUseCents:
        action.payload.mobileServiceFeeHighUseCents ?? null,
      mobileServiceFeeVisitsIncluded:
        action.payload.mobileServiceFeeVisitsIncluded ?? null,
      planYearVisitCount: action.payload.planYearVisitCount ?? null,
      planStartMobileFeeIncluded: !!action.payload.planStartMobileFeeIncluded,
    }),
    setCreatedWorkOrder: (
      state,
      action: PayloadAction<{
        workOrderId: number | null;
        prepaymentToken: string | null;
        existingAccount?: boolean | null;
      }>,
    ) => ({
      ...state,
      workOrderId: action.payload.workOrderId,
      prepaymentToken: action.payload.prepaymentToken,
      existingAccount: action.payload.existingAccount ?? null,
      prepaymentState: 'UNPAID' as PrepaymentUiState,
      prepaidAmountCents: 0,
      // This booking is made: the next one gets a key of its own.
      idempotencyKey: null,
      payerRequest: null,
    }),
    setPrepaymentState: (
      state,
      action: PayloadAction<{
        state: PrepaymentUiState;
        amountCents?: number;
      }>,
    ) => ({
      ...state,
      prepaymentState: action.payload.state,
      prepaidAmountCents: action.payload.amountCents ?? state.prepaidAmountCents,
    }),
    setReferralCode: (state, action: PayloadAction<string | null>) => ({
      ...state,
      referralCode: action.payload,
    }),
    setIdempotencyKey: (state, action: PayloadAction<string | null>) => ({
      ...state,
      idempotencyKey: action.payload,
    }),
    resetBookingPricing: (state) => ({
      ...state,
      slotPrice: null,
      paymentPreference: PaymentPreference.payLater,
      paymentPreferenceTouched: false,
      workOrderId: null,
      prepaymentToken: null,
      existingAccount: null,
      prepaymentState: 'UNPAID' as PrepaymentUiState,
      prepaidAmountCents: 0,
      payerSelected: false,
      payerEmail: '',
      payerName: '',
      payerRequest: null,
    }),
  },
});

export const {
  setBookingSuccess,
  setPotentialFranchiseId,
  setVanId,
  setStoredDiscountAmount,
  setStoredDiscountType,
  setStoredDiscountCodeId,
  setStoredPromoCreditAmount,
  setSlotPrice,
  setPaymentPreference,
  applyDefaultPaymentPreference,
  withdrawPrepay,
  setPrepayDiscountPercent,
  setBestSlotAdjustment,
  setCreatedWorkOrder,
  setPrepaymentState,
  resetBookingPricing,
  setReferralCode,
  setIdempotencyKey,
  setPayerSelected,
  setPayerDetails,
  setPayerRequest,
} = bookingSlice.actions;

export const selectPayerSelected = (state: RootState): boolean => (
  state.booking.payerSelected === true
);
export const selectPayerEmail = (state: RootState): string => (
  state.booking.payerEmail ?? ''
);
export const selectPayerName = (state: RootState): string => (
  state.booking.payerName ?? ''
);
export const selectPayerRequest = (
  state: RootState,
): PayerRequestState | null => state.booking.payerRequest ?? null;

export const selectIdempotencyKey = (
  state: RootState,
): string | null => state.booking.idempotencyKey ?? null;

export const selectReferralCode = (
  state: RootState,
): string | null => state.booking.referralCode ?? null;

export const selectCreatedWorkOrderId = (
  state: RootState,
): number | null => state.booking.workOrderId;
export const selectPrepaymentToken = (
  state: RootState,
): string | null => state.booking.prepaymentToken;
export const selectExistingAccount = (
  state: RootState,
): boolean | null => state.booking.existingAccount ?? null;
export const selectPrepaymentState = (
  state: RootState,
): PrepaymentUiState => state.booking.prepaymentState;
export const selectPrepaidAmountCents = (
  state: RootState,
): number => state.booking.prepaidAmountCents;

// Clears the stored discount / promo credit, dispatching only for values that
// are not already null. Each booking action is a store update, so no-op clears
// would otherwise wake every subscriber for nothing.
export const clearStoredDiscounts = (): AppThunk => (dispatch, getState) => {
  const {
    storedDiscountAmount,
    storedDiscountType,
    storedDiscountCodeId,
    storedPromoCreditAmount,
  } = getState().booking;
  if (storedDiscountAmount !== null) {
    dispatch(setStoredDiscountAmount(null));
  }
  if (storedDiscountType !== null) {
    dispatch(setStoredDiscountType(null));
  }
  if (storedDiscountCodeId !== null) {
    dispatch(setStoredDiscountCodeId(null));
  }
  if (storedPromoCreditAmount !== null) {
    dispatch(setStoredPromoCreditAmount(null));
  }
};

export const selectBookingSucessState = (
  state: RootState,
) => state.booking.success;
export const selectPotentialFranchiseId = (
  state: RootState,
) => state.booking.potentialFranchiseId;
export const selectVanId = (
  state: RootState,
) => state.booking.vanId;
export const selectStoredDiscountAmount = (
  state: RootState,
) => state.booking.storedDiscountAmount;
export const selectStoredDiscountType = (
  state: RootState,
) => state.booking.storedDiscountType;
export const selectStoredDiscountCodeId = (
  state: RootState,
) => state.booking.storedDiscountCodeId;
export const selectStoredPromoCreditAmount = (
  state: RootState,
) => state.booking.storedPromoCreditAmount;
export const selectSlotPrice = (
  state: RootState,
) => state.booking.slotPrice;
export const selectPaymentPreference = (
  state: RootState,
) => state.booking.paymentPreference;
export const selectPaymentPreferenceTouched = (
  state: RootState,
): boolean => state.booking.paymentPreferenceTouched === true;
export const selectPrepayDiscountPercent = (
  state: RootState,
) => state.booking.prepayDiscountPercent;
export const selectBestSlotAdjustmentPercent = (
  state: RootState,
) => state.booking.bestSlotAdjustmentPercent;
export const selectBestSlotAdjustmentLabel = (
  state: RootState,
) => state.booking.bestSlotAdjustmentLabel;
export const selectDiscountCutoffHours = (
  state: RootState,
) => state.booking.discountCutoffHours;
export const selectTaxRatePercent = (
  state: RootState,
): number | null => state.booking.taxRatePercent;
export const selectMobileServiceFeeCents = (
  state: RootState,
): number | null => state.booking.mobileServiceFeeCents;
/**
 * Everything the UI needs to explain the per-visit mobile service fee.
 * Memoized, so it returns the same object until one of the fee inputs
 * changes: components selecting it would otherwise re-render on every store
 * update.
 */
export const selectMobileServiceFeeConfig = createSelector(
  (state: RootState) => state.booking.mobileServiceFeeCents,
  (state: RootState) => state.booking.mobileServiceFeeAllInclusiveCents,
  (state: RootState) => state.booking.mobileServiceFeeHighUseCents,
  (state: RootState) => state.booking.mobileServiceFeeVisitsIncluded,
  (state: RootState) => state.booking.planYearVisitCount,
  (state: RootState) => state.booking.planStartMobileFeeIncluded,
  (
    mobileServiceFeeCents,
    mobileServiceFeeAllInclusiveCents,
    mobileServiceFeeHighUseCents,
    mobileServiceFeeVisitsIncluded,
    planYearVisitCount,
    planStartMobileFeeIncluded,
  ): MobileServiceFeeConfig => ({
    mobileServiceFeeCents,
    mobileServiceFeeAllInclusiveCents,
    mobileServiceFeeHighUseCents,
    mobileServiceFeeVisitsIncluded,
    planYearVisitCount,
    planStartMobileFeeIncluded,
  }),
);

/**
 * The mobile service fee the plan basket's visit is charged, in cents. On a
 * basket that starts a plan that is the first visit's fee: nothing when the
 * onboarding includes it (firstPlanVisitFeeCents, Aperio). Otherwise the
 * All-Inclusive plan's own fee on an All-Inclusive basket, the standard fee
 * on any other (planVisitFeeCents). A number, so components selecting it
 * only re-render when it changes.
 */
export const selectPlanVisitFeeCents = (
  state: RootState,
): number | null => {
  const config = selectMobileServiceFeeConfig(state);
  const basket = state.serviceTypes.services;
  const allInclusive = isAllInclusivePlanBooking(basket);
  return isPlanStartBasket(basket)
    ? firstPlanVisitFeeCents(config, allInclusive)
    : planVisitFeeCents(config, allInclusive);
};

/**
 * True when the basket starts a plan whose onboarding includes this visit's
 * mobile service fee (Aperio): the fee is listed as "included", not charged.
 */
export const selectPlanStartFeeIncluded = (state: RootState): boolean => (
  state.booking.planStartMobileFeeIncluded
  && isPlanStartBasket(state.serviceTypes.services)
);

export default bookingSlice.reducer;
